// src/pages/EadTrilhaPage/EadTrilhaPage.jsx

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowUp, FaArrowDown, FaArrowLeft } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// API
import { useTurmaService } from '../../services/turmaServices';
import { useEmpresaService } from '../../services/empresaServices';
import { useParticipanteService } from '../../services/participanteServices';

import { useTrilhaService } from '../../services/trilhaServices';
import { useModuloService } from '../../services/moduloServices';
import { useModuloSecaoService } from '../../services/moduloSecaoServices';
import { useModuloConteudoService } from '../../services/moduloConteudoServices';
import { useTesteService } from '../../services/testeServices';
import { useParticipanteSecaoConclusaoService } from '../../services/participanteSecaoConclusaoServices';
import { useParticipanteTrilhaStatusService } from '../../services/participanteTrilhaStatusServices';
import { useModuloMiniTesteRespostaService } from '../../services/moduloMiniTesteRespostaServices';

// Constantes
import { URLS } from '../../constants/urls';

// Estilos
import styles from './eadtrilhapage.module.css';

// âœ… ATIVE AQUI
const DEBUG = true;

const debug = {
  log: (...args) => { if (DEBUG) console.log('[EadTrilhaPage]', ...args); },
  warn: (...args) => { if (DEBUG) console.warn('[EadTrilhaPage]', ...args); },
  error: (...args) => { if (DEBUG) console.error('[EadTrilhaPage]', ...args); },
  table: (obj) => { if (DEBUG && console.table) console.table(obj); },
  group: (...args) => { if (DEBUG && console.group) console.group('[EadTrilhaPage]', ...args); },
  groupCollapsed: (...args) => { if (DEBUG && console.groupCollapsed) console.groupCollapsed('[EadTrilhaPage]', ...args); },
  groupEnd: () => { if (DEBUG && console.groupEnd) console.groupEnd(); },
  time: (label) => { if (DEBUG && console.time) console.time(`[EadTrilhaPage] ${label}`); },
  timeEnd: (label) => { if (DEBUG && console.timeEnd) console.timeEnd(`[EadTrilhaPage] ${label}`); },
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const normalizeText = (v) => (v === null || typeof v === 'undefined' ? '' : String(v));
const toSafeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

function extractParticipanteTurmaIdFromItem(item) {
  const direct =
    item?.idParticipanteTurma ??
    item?.id_participante_turma ??
    item?.participanteTurmaId ??
    item?.participante_turma_id ??
    item?.idParticipanteTurmaEncoded ??
    item?.idParticipanteTurmaHash ??
    item?.id_participante_turma_hash ??
    item?.idParticipante_turma ??
    item?.id_vinculo ??
    item?.idVinculo;

  if (direct) return direct;

  // Em alguns retornos, o id do vínculo vem como "id" e o id do participante em outro campo.
  const participanteIdRaw =
    item?.idParticipante ??
    item?.id_participante ??
    item?.participanteId ??
    item?.idUsuario ??
    item?.id_usuario ??
    null;

  if (item?.id && participanteIdRaw && String(item.id) !== String(participanteIdRaw)) {
    return item.id;
  }

  return null;
}

function resolveParticipanteTrilhaStatus(percentual) {
  const pct = Math.max(0, Math.min(100, Number(percentual) || 0));
  if (pct <= 0) return 'nao_iniciada';
  if (pct >= 100) return 'concluida';
  return 'em_andamento';
}

function parseQuestionsStructure(raw) {
  if (raw === null || typeof raw === 'undefined') return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null') return null;
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed?.questions) ? parsed : null;
    } catch (e) {
      return null;
    }
  }
  if (typeof raw === 'object') {
    return Array.isArray(raw?.questions) ? raw : null;
  }
  return null;
}

function parseJsonLoose(raw) {
  if (raw === null || typeof raw === 'undefined') return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null') return null;
    try { return JSON.parse(trimmed); } catch { return null; }
  }
  if (typeof raw === 'object') return raw;
  return null;
}

function findQuestionIndexByIdOrText(questions, questionId, questionText) {
  const list = Array.isArray(questions) ? questions : [];
  if (questionId) {
    const idxById = list.findIndex((q) => String(q?.id ?? q?.questionId ?? q?.qid ?? '') === String(questionId));
    if (idxById >= 0) return idxById;
  }
  if (questionText) {
    const idxByText = list.findIndex((q) => String(q?.question ?? '').trim() === String(questionText).trim());
    if (idxByText >= 0) return idxByText;
  }
  return -1;
}

function coerceSavedAnswerByQuestion(questionObj, rawValue) {
  if (!questionObj) return rawValue;
  const type = questionObj?.type;

  if (type === 'closed') return String(rawValue ?? '');

  if (type === 'priority') {
    if (Array.isArray(rawValue)) return rawValue.map((v) => String(v));
    if (typeof rawValue === 'string') {
      return rawValue
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
    }
    return rawValue;
  }

  if (type === 'scale') {
    const n = Number(rawValue);
    return Number.isFinite(n) ? n : rawValue;
  }

  if (type === 'open' && questionObj?.hasDate) {
    if (rawValue && typeof rawValue === 'object') {
      return {
        text: String(rawValue?.text ?? ''),
        date: String(rawValue?.date ?? ''),
      };
    }
    return { text: String(rawValue ?? ''), date: '' };
  }

  return rawValue;
}

function normalizeMiniTesteRespostasFromPersisted(questions, estruturaResp) {
  const list = Array.isArray(questions) ? questions : [];
  const normalized = {};
  const respostasRaw = estruturaResp?.respostas ?? estruturaResp?.answers ?? null;

  if (Array.isArray(respostasRaw)) {
    respostasRaw.forEach((value, idx) => {
      const q = list[idx];
      normalized[idx] = coerceSavedAnswerByQuestion(q, value);
    });
  } else if (respostasRaw && typeof respostasRaw === 'object') {
    Object.entries(respostasRaw).forEach(([k, value]) => {
      const asNumber = Number(k);
      if (Number.isInteger(asNumber) && asNumber >= 0) {
        normalized[asNumber] = coerceSavedAnswerByQuestion(list[asNumber], value);
        return;
      }
      const idxById = findQuestionIndexByIdOrText(list, k, null);
      if (idxById >= 0) {
        normalized[idxById] = coerceSavedAnswerByQuestion(list[idxById], value);
      }
    });
  }

  const respostasFormatadas = Array.isArray(estruturaResp?.respostasFormatadas)
    ? estruturaResp.respostasFormatadas
    : [];

  respostasFormatadas.forEach((item) => {
    const idx = findQuestionIndexByIdOrText(list, item?.id, item?.pergunta);
    if (idx < 0) return;
    if (typeof normalized[idx] !== 'undefined') return;
    normalized[idx] = coerceSavedAnswerByQuestion(list[idx], item?.resposta);
  });

  return normalized;
}

function extractParticipanteModuloTesteRespostaMeta(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const id =
    raw?.id ??
    raw?.idParticipanteModuloTesteResposta ??
    raw?.id_participante_modulo_teste_resposta ??
    null;

  const idModulo = raw?.idModulo ?? raw?.id_modulo ?? null;
  const idTeste = raw?.idTeste ?? raw?.id_teste ?? null;
  const idTrilha = raw?.idTrilha ?? raw?.id_trilha ?? null;

  return { id, idModulo, idTeste, idTrilha };
}

function getQuestionCorrectAnswer(questionObj) {
  const value =
    questionObj?.alternativaCorreta ??
    questionObj?.alternativa_correta ??
    questionObj?.respostaCorreta ??
    questionObj?.resposta_correta ??
    questionObj?.correctOption ??
    questionObj?.correct_option ??
    questionObj?.correctAnswer ??
    questionObj?.correct_answer ??
    null;

  if (value === null || typeof value === 'undefined' || value === '') return null;
  if (Array.isArray(value)) return value.map((v) => String(v));
  return String(value);
}

function evaluateIfAnswerIsCorrect(questionObj, userValue, correctValue) {
  if (correctValue === null || typeof correctValue === 'undefined') return null;

  const type = questionObj?.type;
  if (type === 'closed') {
    return String(userValue ?? '') === String(correctValue);
  }

  if (type === 'priority') {
    const userList = Array.isArray(userValue) ? userValue.map((v) => String(v)) : [];
    const correctList = Array.isArray(correctValue)
      ? correctValue.map((v) => String(v))
      : String(correctValue)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);

    if (userList.length !== correctList.length) return false;
    return userList.every((v, idx) => v === correctList[idx]);
  }

  if (type === 'scale') {
    return Number(userValue) === Number(correctValue);
  }

  // Questões abertas podem não ter gabarito automático.
  return null;
}

function isAbsoluteLike(url) {
  const u = String(url || '').trim();
  return (
    /^https?:\/\//i.test(u) ||
    /^data:/i.test(u) ||
    /^blob:/i.test(u) ||
    /^\/\//.test(u)
  );
}

function joinBaseAndPath(base, path) {
  const b = String(base || '');
  const p = String(path || '');
  if (!b) return p;
  if (!p) return '';

  const b2 = b.endsWith('/') ? b.slice(0, -1) : b;
  const p2 = p.startsWith('/') ? p.slice(1) : p;
  return `${b2}/${p2}`;
}

// Regra: se vier relativo, prefixa com URLS.IMG_PATH
function toAssetUrl(maybePath) {
  const raw = String(maybePath || '').trim();
  if (!raw) return '';

  if (isAbsoluteLike(raw)) return raw;

  const base = String(URLS?.IMG_PATH || '').trim();
  if (base && raw.startsWith(base)) return raw;

  return joinBaseAndPath(base, raw);
}

function looksLikePdf(pathOrUrl) {
  const v = String(pathOrUrl || '').toLowerCase();
  return v.endsWith('.pdf') || v.includes('/reports/') || v.includes('reports/');
}

function PdfViewerModal({ open, title, url, onClose }) {
  // Fecha com ESC
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.pdfOverlay} onMouseDown={onClose}>
      <div
        className={styles.pdfModal}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.pdfHeader}>
          <div className={styles.pdfTitle}>
            {title || 'Visualizador de PDF'}
          </div>

          <div className={styles.pdfActions}>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className={styles.pdfActionBtn}
              title="Abrir em nova aba"
            >
              Abrir
            </a>

            <a
              href={url}
              download
              className={styles.pdfActionBtnOutline}
              title="Baixar PDF"
            >
              Baixar
            </a>

            <button
              type="button"
              className={styles.pdfCloseBtn}
              onClick={onClose}
              title="Fechar (ESC)"
            >
              ✕
            </button>
          </div>
        </div>

        <div className={styles.pdfBody}>
          <iframe
            title={title || 'pdf'}
            src={url}
            className={styles.pdfFrame}
          />
        </div>
      </div>
    </div>
  );
}

function EadTrilhaPage() {
  const { idTurma } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { logout, usuario, token } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();

  const { obterTurmaPorId, associarParticipanteATurma } = useTurmaService();
  const { obterEmpresaPorId } = useEmpresaService();
  const {
    obterParticipanteTurmaPorParticipanteETurma,
    readModuloMiniTesteRespostasByModuloUsuario,
    readModuloMiniTesteRespostaUltimaTentativa,
    upsertParticipanteModuloTesteResposta,
    readParticipanteModuloTesteRespostaByParticipanteAndModulo,
    readParticipanteModuloTesteRespostaByParticipanteAndTrilha,
  } = useParticipanteService();

  const { obterTrilhaPorId } = useTrilhaService();
  const { listarModulosPorTrilha } = useModuloService();
  const { listarSecoesPorModulo } = useModuloSecaoService();
  const { listarConteudosPorSecao } = useModuloConteudoService();
  const { obterTestePorId } = useTesteService();
  const { listarSecoesConcluidasPorParticipanteTurmaEModulo, marcarSecaoConcluida, marcarSecaoNaoConcluida } = useParticipanteSecaoConclusaoService();
  const { setStatusEPercentual, obterTrilhaStatusPorParticipanteTurmaETrilha } = useParticipanteTrilhaStatusService();
  const {
    criarModuloMiniTesteResposta,
  } = useModuloMiniTesteRespostaService();

  const [empresa, setEmpresa] = useState(null);
  const [turma, setTurma] = useState(null);

  const [trilha, setTrilha] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [idParticipanteTurma, setIdParticipanteTurma] = useState(null);
  const [moduloProgressMap, setModuloProgressMap] = useState({});
  const [trilhaProgressPercent, setTrilhaProgressPercent] = useState(0);
  const [trilhaProgressStatus, setTrilhaProgressStatus] = useState('nao_iniciada');
  const [secoesByModulo, setSecoesByModulo] = useState({});
  const [secoesConcluidasByModulo, setSecoesConcluidasByModulo] = useState({});
  const [loadingProgresso, setLoadingProgresso] = useState(false);
  const [pendingSecaoActions, setPendingSecaoActions] = useState({});
  const [miniTesteRespostaMetaByModulo, setMiniTesteRespostaMetaByModulo] = useState({});
  const [miniTesteSubmittingByModulo, setMiniTesteSubmittingByModulo] = useState({});
  const [testeExternoRespostaMetaByModulo, setTesteExternoRespostaMetaByModulo] = useState({});
  const [testeExternoSubmittingByModulo, setTesteExternoSubmittingByModulo] = useState({});

  const [selectedModuloId, setSelectedModuloId] = useState(null);

  // Cache de detalhes por módulo (evita refetch ao voltar/abrir de novo)
  // âœ… agora também guarda testeEstrutura (questions etc)
  const [detalhesCache, setDetalhesCache] = useState({});

  // PDF viewer
  const [pdfViewer, setPdfViewer] = useState({
    open: false,
    url: '',
    title: ''
  });

  const openPdf = (url, title) => {
    if (!url) return;
    debug.log('openPdf ->', { url, title });
    setPdfViewer({ open: true, url, title: title || 'PDF' });
  };

  const closePdf = () => setPdfViewer({ open: false, url: '', title: '' });

  // ðŸ”Ž contador local de loader (ajuda a detectar â€œvazamentoâ€)
  const loaderCountRef = useRef(0);
  const progressPersistLockRef = useRef(false);
  const lastPersistedProgressRef = useRef({ percentual: null, status: null });
  const missingIdParticipanteTurmaWarnedRef = useRef(false);
  const showL = (msg) => {
    loaderCountRef.current += 1;
    debug.log(`showLoader(${loaderCountRef.current}) ->`, msg);
    showLoader(msg);
  };
  const hideL = () => {
    loaderCountRef.current = Math.max(0, loaderCountRef.current - 1);
    debug.log(`hideLoader(${loaderCountRef.current})`);
    hideLoader();
  };

  const selectedModulo = useMemo(() => {
    if (!selectedModuloId) return null;
    return modulos.find((m) => String(m.id) === String(selectedModuloId)) || null;
  }, [selectedModuloId, modulos]);

  const detalhesSelecionado = useMemo(() => {
    if (!selectedModuloId) return null;
    return detalhesCache[selectedModuloId] || null;
  }, [selectedModuloId, detalhesCache]);

  // ===== Helpers =====
  const sortByOrdem = (a, b) => {
    const oa = Number(a?.ordem ?? 0);
    const ob = Number(b?.ordem ?? 0);
    if (oa !== ob) return oa - ob;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  };

  // âœ… (NOVO) módulo fechado -> desabilitar card
  const isModuloFechado = (m) => Boolean(m?.fechado);

  const getModuloDataAberturaLabel = (m) => {
    const d = m?.dataAbertura ?? m?.data_abertura ?? '';
    return d ? String(d) : '';
  };

  // Prioriza percentual vindo da API (quando disponível) e usa mock como fallback.
  // Facilita integração futura: basta backend enviar um dos campos abaixo.
  const getModuloCompletionPercent = (modulo, idx) => {
    const moduloId = String(modulo?.id || '');
    if (moduloId && typeof moduloProgressMap[moduloId] === 'number') {
      return Math.max(0, Math.min(100, Math.round(moduloProgressMap[moduloId])));
    }

    const apiCandidates = [
      modulo?.percentualConclusao,
      modulo?.percentual_conclusao,
      modulo?.progressoConclusao,
      modulo?.progresso_conclusao,
      modulo?.completionPercentage,
      modulo?.progressPercent,
    ];

    const apiValue = apiCandidates.find((v) => Number.isFinite(Number(v)));
    if (apiValue !== undefined) {
      return Math.max(0, Math.min(100, Math.round(Number(apiValue))));
    }

    // Mock estável por posição (evita mudar a cada render)
    const mock = ((idx + 2) * 19) % 101;
    return Math.max(6, Math.min(98, mock));
  };

  const computeModuloPercent = (idModulo, mapSecoes, mapConcluidas) => {
    const moduloKey = String(idModulo || '');
    const secoes = safeArray(mapSecoes?.[moduloKey]);
    const concluidasSet = new Set(safeArray(mapConcluidas?.[moduloKey]).map((s) => String(s)));

    const total = secoes.length;
    if (total <= 0) return 0;

    const concluidaCount = secoes.reduce((acc, s) => {
      const secaoId = String(s?.id || s?.idSecao || '');
      return secaoId && concluidasSet.has(secaoId) ? acc + 1 : acc;
    }, 0);
    return Math.max(0, Math.min(100, (concluidaCount / total) * 100));
  };

  const computeAllModuloProgress = (modulosLista, mapSecoes, mapConcluidas) => {
    const next = {};
    safeArray(modulosLista).forEach((m) => {
      const idModulo = String(m?.id || '');
      if (!idModulo) return;
      next[idModulo] = computeModuloPercent(idModulo, mapSecoes, mapConcluidas);
    });
    return next;
  };

  const computeTrilhaPercentual = (progressMap, modulosLista) => {
    const percents = safeArray(modulosLista)
      .map((m) => progressMap[String(m?.id || '')])
      .filter((v) => Number.isFinite(Number(v)))
      .map((v) => Number(v));
    if (!percents.length) return 0;
    const avg = percents.reduce((acc, v) => acc + v, 0) / percents.length;
    return Math.max(0, Math.min(100, Number(avg.toFixed(2))));
  };

  // âœ… Redireciona para login mantendo â€œfromâ€
  const redirectToLogin = (reason = '') => {
    debug.groupCollapsed('redirectToLogin');
    debug.log('reason:', reason);
    debug.log('idTurma:', idTurma);
    debug.log('location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
    debug.groupEnd();

    navigate(
      `/participante/login?turma=${encodeURIComponent(idTurma || '')}&dest=ead`,
      { replace: true, state: { from: location } }
    );
  };

  const handleApiFailure = (msg, fallbackMsg) => {
    const message = msg || fallbackMsg;
    if (String(message || '').toLowerCase().includes('token')) {
      logout();
      showToast('Sua sessão expirou. Faça login novamente.', 3500, 'error');
      redirectToLogin('api token');
      return true;
    }
    showToast(message || fallbackMsg || 'Não foi possível concluir a operação.', 3500, 'error');
    return false;
  };

  const apiCall = async (label, payload, requestFn) => {
    debug.groupCollapsed(`API ${label}`);
    debug.log('REQ payload ->', payload);
    try {
      const response = await requestFn();
      debug.log('RES payload ->', response);
      return response;
    } catch (error) {
      debug.error('ERR payload ->', error);
      throw error;
    } finally {
      debug.groupEnd();
    }
  };

  const isRespostaNaoEncontrada = (message) =>
    String(message || '').toLowerCase().includes('resposta nao encontrada');

  const persistTrilhaProgress = async (percentualValue) => {
    if (!trilha?.id) return false;
    if (!idParticipanteTurma) {
      debug.warn('persistTrilhaProgress ignorado: idParticipanteTurma ausente', {
        idParticipanteTurma,
        idTrilha: trilha?.id,
      });
      return false;
    }

    const percentual = Math.max(0, Math.min(100, Number(percentualValue) || 0));
    const status = resolveParticipanteTrilhaStatus(percentual);
    const last = lastPersistedProgressRef.current;
    if (last.percentual === percentual && last.status === status) return true;

    if (progressPersistLockRef.current) return false;
    progressPersistLockRef.current = true;

    try {
      const statusPayload = {
        idParticipanteTurma,
        idTrilha: trilha.id,
        status,
        percentual,
      };
      const resp = await apiCall(
        'setStatusEPercentual',
        statusPayload,
        () => setStatusEPercentual(statusPayload)
      );

      if (!resp?.success) {
        handleApiFailure(resp?.message, 'Não foi possível persistir o progresso da trilha.');
        return false;
      }

      lastPersistedProgressRef.current = { percentual, status };
      return true;
    } catch (e) {
      debug.error('persistTrilhaProgress erro:', e);
      showToast('Erro de rede ao persistir progresso da trilha.', 3500, 'error');
      return false;
    } finally {
      progressPersistLockRef.current = false;
    }
  };

  // YouTube: converte qualquer URL (watch/youtu.be/shorts/embed) para embed
  const toYouTubeEmbedUrl = (inputUrl) => {
    if (!inputUrl) return null;

    try {
      const u = new URL(inputUrl);

      if (u.hostname.includes('youtu.be')) {
        const videoId = u.pathname.replace('/', '').trim();
        if (!videoId) return null;
        const list = u.searchParams.get('list');
        return `https://www.youtube.com/embed/${videoId}${list ? `?list=${list}` : ''}`;
      }

      if (u.hostname.includes('youtube.com')) {
        if (u.pathname === '/watch') {
          const videoId = u.searchParams.get('v');
          if (!videoId) return null;
          const list = u.searchParams.get('list');
          return `https://www.youtube.com/embed/${videoId}${list ? `?list=${list}` : ''}`;
        }

        if (u.pathname.startsWith('/shorts/')) {
          const videoId = u.pathname.split('/shorts/')[1]?.split('/')[0]?.trim();
          if (!videoId) return null;
          return `https://www.youtube.com/embed/${videoId}`;
        }

        if (u.pathname.startsWith('/embed/')) return inputUrl;
      }

      return null;
    } catch (e) {
      debug.warn('toYouTubeEmbedUrl: URL inválida:', inputUrl, e);
      return null;
    }
  };

  // ====== (NOVO) LÓGICA DE TESTE EXTERNO DENTRO DO MÓDULO ======
  const EMBARALHAR_OPCOES = false;

  const [respostasTeste, setRespostasTeste] = useState({});
  const [paginaAtualTeste, setPaginaAtualTeste] = useState(0);
  const [embaralhamentoAtualTeste, setEmbaralhamentoAtualTeste] = useState({});
  const [dragStateTeste, setDragStateTeste] = useState({ fromIdx: null, overIdx: null });

  const [respostasMiniTeste, setRespostasMiniTeste] = useState({});
  const [paginaAtualMiniTeste, setPaginaAtualMiniTeste] = useState(0);
  const [embaralhamentoAtualMiniTeste, setEmbaralhamentoAtualMiniTeste] = useState({});
  const [dragStateMiniTeste, setDragStateMiniTeste] = useState({ fromIdx: null, overIdx: null });

  const testeEstrutura = detalhesSelecionado?.testeEstrutura || null;
  const totalQuestoesTeste = testeEstrutura?.questions?.length || 0;
  const miniTesteEstrutura = detalhesSelecionado?.miniTesteEstrutura
    || parseQuestionsStructure(selectedModulo?.mini_teste_estrutura ?? selectedModulo?.miniTesteEstrutura ?? null);
  const totalQuestoesMiniTeste = miniTesteEstrutura?.questions?.length || 0;

  const perguntaAtualTeste = testeEstrutura?.questions?.[paginaAtualTeste];
  const respostaAtualTeste = respostasTeste[paginaAtualTeste];
  const perguntaAtualMiniTeste = miniTesteEstrutura?.questions?.[paginaAtualMiniTeste];
  const respostaAtualMiniTeste = respostasMiniTeste[paginaAtualMiniTeste];
  const miniTesteAlreadySubmitted = Boolean(
    miniTesteRespostaMetaByModulo[String(selectedModulo?.id || '')]?.id
  );
  const testeExternoMetaAtual = testeExternoRespostaMetaByModulo[String(selectedModulo?.id || '')] || null;
  const testeExternoHasRespostaSalva = Boolean(testeExternoMetaAtual?.id);

  const handleChangeTeste = (index, value) => {
    setRespostasTeste(prev => ({ ...prev, [index]: value }));
  };

  const handleChangeMiniTeste = (index, value) => {
    if (miniTesteAlreadySubmitted) return;
    setRespostasMiniTeste(prev => ({ ...prev, [index]: value }));
  };

  const movePriorityItemTeste = (fromIdx, toIdx) => {
    setRespostasTeste(prev => {
      const atualPergunta = testeEstrutura?.questions?.[paginaAtualTeste];
      if (atualPergunta?.type !== 'priority') return prev;

      const currentOrder = Array.isArray(prev[paginaAtualTeste])
        ? prev[paginaAtualTeste]
        : Object.keys((atualPergunta.options) || {});

      if (toIdx < 0 || toIdx >= currentOrder.length) return prev;
      const nova = [...currentOrder];
      const [m] = nova.splice(fromIdx, 1);
      nova.splice(toIdx, 0, m);
      return { ...prev, [paginaAtualTeste]: nova };
    });
  };

  const handleClickUpTeste = (idx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    movePriorityItemTeste(idx, idx - 1);
  };

  const handleClickDownTeste = (idx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    movePriorityItemTeste(idx, idx + 1);
  };

  const movePriorityItemMiniTeste = (fromIdx, toIdx) => {
    if (miniTesteAlreadySubmitted) return;
    setRespostasMiniTeste(prev => {
      const atualPergunta = miniTesteEstrutura?.questions?.[paginaAtualMiniTeste];
      if (atualPergunta?.type !== 'priority') return prev;

      const currentOrder = Array.isArray(prev[paginaAtualMiniTeste])
        ? prev[paginaAtualMiniTeste]
        : Object.keys((atualPergunta.options) || {});

      if (toIdx < 0 || toIdx >= currentOrder.length) return prev;
      const nova = [...currentOrder];
      const [m] = nova.splice(fromIdx, 1);
      nova.splice(toIdx, 0, m);
      return { ...prev, [paginaAtualMiniTeste]: nova };
    });
  };

  const handleClickUpMiniTeste = (idx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    movePriorityItemMiniTeste(idx, idx - 1);
  };

  const handleClickDownMiniTeste = (idx) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    movePriorityItemMiniTeste(idx, idx + 1);
  };

  const isPerguntaRespondida = (perguntaObj, respostaVal) => {
    if (!perguntaObj) return false;
    const tipo = perguntaObj.type;

    if (tipo === 'closed') return (respostaVal ?? '').toString().trim() !== '';
    if (tipo === 'priority') return Array.isArray(respostaVal) && respostaVal.length > 0;
    if (tipo === 'scale') return respostaVal !== undefined && respostaVal !== null && respostaVal !== '';
    if (tipo === 'open') {
      if (perguntaObj.hasDate) {
        if (!respostaVal || typeof respostaVal !== 'object') return false;
        const text = (respostaVal.text ?? '').toString().trim();
        const date = (respostaVal.date ?? '').toString().trim();
        return text !== '' && date !== '';
      }
      return (respostaVal ?? '').toString().trim() !== '';
    }
    return respostaVal !== undefined && respostaVal !== null && respostaVal !== '';
  };

  const perguntaRespondidaTeste = isPerguntaRespondida(perguntaAtualTeste, respostaAtualTeste);
  const perguntaRespondidaMiniTeste = isPerguntaRespondida(perguntaAtualMiniTeste, respostaAtualMiniTeste);

  const priorityOrderTeste = (perguntaAtualTeste?.type === 'priority')
    ? (Array.isArray(respostasTeste[paginaAtualTeste])
      ? respostasTeste[paginaAtualTeste]
      : Object.keys(perguntaAtualTeste?.options || {}))
    : [];

  const priorityOrderMiniTeste = (perguntaAtualMiniTeste?.type === 'priority')
    ? (Array.isArray(respostasMiniTeste[paginaAtualMiniTeste])
      ? respostasMiniTeste[paginaAtualMiniTeste]
      : Object.keys(perguntaAtualMiniTeste?.options || {}))
    : [];

  // Reset do estado do teste quando troca de módulo
  useEffect(() => {
    setRespostasTeste({});
    setPaginaAtualTeste(0);
    setEmbaralhamentoAtualTeste({});
    setDragStateTeste({ fromIdx: null, overIdx: null });
    setRespostasMiniTeste({});
    setPaginaAtualMiniTeste(0);
    setEmbaralhamentoAtualMiniTeste({});
    setDragStateMiniTeste({ fromIdx: null, overIdx: null });
  }, [selectedModuloId]);

  // Embaralhamento + valores iniciais (igual ao ResponderTestePage)
  useEffect(() => {
    const p = testeEstrutura?.questions?.[paginaAtualTeste];
    if (!p) {
      setEmbaralhamentoAtualTeste({});
      return;
    }

    if (p.type === 'closed') {
      const opcoesOriginais = Object.entries(p.options || {});
      const textos = opcoesOriginais.map(([, texto]) => texto);
      const textosProcessados = EMBARALHAR_OPCOES ? [...textos].sort(() => Math.random() - 0.5) : textos;

      const novaOrdem = {};
      Object.keys(p.options || {}).forEach((letra, index) => {
        novaOrdem[letra] = textosProcessados[index];
      });
      setEmbaralhamentoAtualTeste(novaOrdem);

      setRespostasTeste(prev => (prev[paginaAtualTeste] !== undefined ? prev : { ...prev, [paginaAtualTeste]: '' }));
    } else if (p.type === 'priority') {
      setRespostasTeste(prev => {
        if (Array.isArray(prev[paginaAtualTeste])) return prev;
        const initialOrder = Object.keys(p.options || {});
        return { ...prev, [paginaAtualTeste]: initialOrder };
      });
      setEmbaralhamentoAtualTeste({});
    } else if (p.type === 'scale') {
      setEmbaralhamentoAtualTeste({});
      setRespostasTeste(prev => {
        if (prev[paginaAtualTeste] !== undefined) return prev;
        const min = (p.scale && typeof p.scale.min !== 'undefined')
          ? Number(p.scale.min)
          : 0;
        return { ...prev, [paginaAtualTeste]: min };
      });
    } else if (p.type === 'open') {
      setEmbaralhamentoAtualTeste({});
      setRespostasTeste(prev => {
        if (prev[paginaAtualTeste] !== undefined) return prev;
        if (p.hasDate) return { ...prev, [paginaAtualTeste]: { text: '', date: '' } };
        return { ...prev, [paginaAtualTeste]: '' };
      });
    } else {
      setEmbaralhamentoAtualTeste({});
      setRespostasTeste(prev => (prev[paginaAtualTeste] !== undefined ? prev : { ...prev, [paginaAtualTeste]: '' }));
    }
  }, [paginaAtualTeste, testeEstrutura]);

  useEffect(() => {
    const p = miniTesteEstrutura?.questions?.[paginaAtualMiniTeste];
    if (!p) {
      setEmbaralhamentoAtualMiniTeste({});
      return;
    }

    if (p.type === 'closed') {
      const opcoesOriginais = Object.entries(p.options || {});
      const textos = opcoesOriginais.map(([, texto]) => texto);
      const textosProcessados = EMBARALHAR_OPCOES ? [...textos].sort(() => Math.random() - 0.5) : textos;

      const novaOrdem = {};
      Object.keys(p.options || {}).forEach((letra, index) => {
        novaOrdem[letra] = textosProcessados[index];
      });
      setEmbaralhamentoAtualMiniTeste(novaOrdem);

      setRespostasMiniTeste(prev => (prev[paginaAtualMiniTeste] !== undefined ? prev : { ...prev, [paginaAtualMiniTeste]: '' }));
    } else if (p.type === 'priority') {
      setRespostasMiniTeste(prev => {
        if (Array.isArray(prev[paginaAtualMiniTeste])) return prev;
        const initialOrder = Object.keys(p.options || {});
        return { ...prev, [paginaAtualMiniTeste]: initialOrder };
      });
      setEmbaralhamentoAtualMiniTeste({});
    } else if (p.type === 'scale') {
      setEmbaralhamentoAtualMiniTeste({});
      setRespostasMiniTeste(prev => {
        if (prev[paginaAtualMiniTeste] !== undefined) return prev;
        const min = (p.scale && typeof p.scale.min !== 'undefined')
          ? Number(p.scale.min)
          : 0;
        return { ...prev, [paginaAtualMiniTeste]: min };
      });
    } else if (p.type === 'open') {
      setEmbaralhamentoAtualMiniTeste({});
      setRespostasMiniTeste(prev => {
        if (prev[paginaAtualMiniTeste] !== undefined) return prev;
        if (p.hasDate) return { ...prev, [paginaAtualMiniTeste]: { text: '', date: '' } };
        return { ...prev, [paginaAtualMiniTeste]: '' };
      });
    } else {
      setEmbaralhamentoAtualMiniTeste({});
      setRespostasMiniTeste(prev => (prev[paginaAtualMiniTeste] !== undefined ? prev : { ...prev, [paginaAtualMiniTeste]: '' }));
    }
  }, [paginaAtualMiniTeste, miniTesteEstrutura]);

  const onDragStartPriorityTeste = (fromIdx) => (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(fromIdx));
    setDragStateTeste({ fromIdx, overIdx: null });
  };

  const onDragOverPriorityTeste = (overIdx) => (e) => {
    e.preventDefault();
    setDragStateTeste(cur => ({ ...cur, overIdx }));
  };

  const onDropPriorityTeste = (toIdx) => (e) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    setRespostasTeste(prev => {
      const atual = Array.isArray(prev[paginaAtualTeste])
        ? prev[paginaAtualTeste]
        : Object.keys((testeEstrutura?.questions?.[paginaAtualTeste]?.options) || {});
      if (from === toIdx || from < 0 || toIdx < 0 || from >= atual.length || toIdx >= atual.length) return prev;
      const nova = [...atual];
      const [m] = nova.splice(from, 1);
      nova.splice(toIdx, 0, m);
      return { ...prev, [paginaAtualTeste]: nova };
    });
    setDragStateTeste({ fromIdx: null, overIdx: null });
  };

  const onDragEndPriorityTeste = () => setDragStateTeste({ fromIdx: null, overIdx: null });

  const onDragStartPriorityMiniTeste = (fromIdx) => (e) => {
    if (miniTesteAlreadySubmitted) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(fromIdx));
    setDragStateMiniTeste({ fromIdx, overIdx: null });
  };

  const onDragOverPriorityMiniTeste = (overIdx) => (e) => {
    if (miniTesteAlreadySubmitted) return;
    e.preventDefault();
    setDragStateMiniTeste(cur => ({ ...cur, overIdx }));
  };

  const onDropPriorityMiniTeste = (toIdx) => (e) => {
    if (miniTesteAlreadySubmitted) return;
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'));
    setRespostasMiniTeste(prev => {
      const atual = Array.isArray(prev[paginaAtualMiniTeste])
        ? prev[paginaAtualMiniTeste]
        : Object.keys((miniTesteEstrutura?.questions?.[paginaAtualMiniTeste]?.options) || {});
      if (from === toIdx || from < 0 || toIdx < 0 || from >= atual.length || toIdx >= atual.length) return prev;
      const nova = [...atual];
      const [m] = nova.splice(from, 1);
      nova.splice(toIdx, 0, m);
      return { ...prev, [paginaAtualMiniTeste]: nova };
    });
    setDragStateMiniTeste({ fromIdx: null, overIdx: null });
  };

  const onDragEndPriorityMiniTeste = () => setDragStateMiniTeste({ fromIdx: null, overIdx: null });

  const handleAnteriorTeste = () => {
    if (paginaAtualTeste > 0) setPaginaAtualTeste(paginaAtualTeste - 1);
  };

  const handleProximoTeste = () => {
    if (!testeEstrutura?.questions?.length) return;
    if (paginaAtualTeste >= totalQuestoesTeste - 1) return;

    const p = testeEstrutura.questions[paginaAtualTeste];
    const r = respostasTeste[paginaAtualTeste];
    if (isPerguntaRespondida(p, r)) setPaginaAtualTeste(paginaAtualTeste + 1);
  };

  const handleAnteriorMiniTeste = () => {
    if (paginaAtualMiniTeste > 0) setPaginaAtualMiniTeste(paginaAtualMiniTeste - 1);
  };

  const handleProximoMiniTeste = () => {
    if (!miniTesteEstrutura?.questions?.length) return;
    if (paginaAtualMiniTeste >= totalQuestoesMiniTeste - 1) return;

    const p = miniTesteEstrutura.questions[paginaAtualMiniTeste];
    const r = respostasMiniTeste[paginaAtualMiniTeste];
    if (isPerguntaRespondida(p, r)) setPaginaAtualMiniTeste(paginaAtualMiniTeste + 1);
  };

  const buildScaleConfig = (questionObj, answerValue) => {
    if (questionObj?.type !== 'scale') return null;
    const min = Number(questionObj?.scale?.min ?? 0);
    const max = Number(questionObj?.scale?.max ?? 10);
    const labels = (questionObj?.scale?.labels) || {};
    const valueNum = Number(
      answerValue !== undefined && answerValue !== null && answerValue !== ''
        ? answerValue
        : min
    );

    const ticks = [];
    for (let v = min; v <= max; v++) {
      const lbl = labels[v] ?? String(v);
      ticks.push({ value: v, label: lbl });
    }

    const minLabel = labels[min] ?? String(min);
    const maxLabel = labels[max] ?? String(max);
    const currentLabel = labels[valueNum] ?? String(valueNum);
    return { min, max, ticks, minLabel, maxLabel, valueNum, currentLabel };
  };

  const buildFormattedAnswers = (estrutura, respostasObj) => {
    return Object.entries(respostasObj).map(([indiceStr, valor]) => {
      const indice = Number(indiceStr);
      const perguntaObj = estrutura?.questions?.[indice];

      if (!perguntaObj) {
        return { pergunta: '(desconhecida)', resposta: valor, textoResposta: valor };
      }

      const perguntaId = perguntaObj.id ?? perguntaObj.questionId ?? perguntaObj.qid ?? null;
      const respostaCorreta = getQuestionCorrectAnswer(perguntaObj);
      const acertou = evaluateIfAnswerIsCorrect(perguntaObj, valor, respostaCorreta);

      if (perguntaObj.type === 'priority') {
        const orderLetters = Array.isArray(valor) ? valor : Object.keys(perguntaObj.options || {});
        const orderTexts = orderLetters.map((L) => (perguntaObj.options || {})[L]);
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'priority',
          resposta: orderLetters,
          textoResposta: orderTexts,
          respostaCorreta,
          acertou,
        };
      }

      if (perguntaObj.type === 'closed') {
        const letra = valor;
        const textoResposta = (perguntaObj.options || {})[letra];
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'closed',
          resposta: letra,
          textoResposta,
          respostaCorreta,
          acertou,
        };
      }

      if (perguntaObj.type === 'scale') {
        const numVal = Number(valor);
        const labels = (perguntaObj.scale && perguntaObj.scale.labels) || {};
        const textoResposta = labels[numVal] ?? String(numVal);
        return {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'scale',
          resposta: numVal,
          textoResposta,
          respostaCorreta,
          acertou,
        };
      }

      if (perguntaObj.type === 'open') {
        let texto = '';
        let dataResposta = undefined;

        if (valor && typeof valor === 'object') {
          texto = (valor.text ?? '').toString();
          dataResposta = (valor.date ?? '') || undefined;
        } else {
          texto = (valor ?? '').toString();
        }

        const base = {
          id: perguntaId || undefined,
          pergunta: perguntaObj.question,
          tipo: 'open',
          resposta: texto,
          textoResposta: texto,
          respostaCorreta,
          acertou,
        };

        if (perguntaObj.hasDate && dataResposta) {
          return { ...base, dataResposta };
        }

        return base;
      }

      return {
        id: perguntaId || undefined,
        pergunta: perguntaObj.question,
        tipo: perguntaObj.type || 'desconhecido',
        resposta: valor,
        textoResposta: valor,
        respostaCorreta,
        acertou,
      };
    });
  };

  const recalculateProgressAndPersist = async (nextConcluidasByModulo, nextSecoesByModulo = secoesByModulo) => {
    const nextModuloProgress = computeAllModuloProgress(modulos, nextSecoesByModulo, nextConcluidasByModulo);
    const nextTrilhaPercent = computeTrilhaPercentual(nextModuloProgress, modulos);
    const nextTrilhaStatus = resolveParticipanteTrilhaStatus(nextTrilhaPercent);

    setSecoesConcluidasByModulo(nextConcluidasByModulo);
    setModuloProgressMap(nextModuloProgress);
    setTrilhaProgressPercent(nextTrilhaPercent);
    setTrilhaProgressStatus(nextTrilhaStatus);

    await persistTrilhaProgress(nextTrilhaPercent);
  };

  const isSecaoConcluida = (idModulo, idSecao) => {
    const moduloKey = String(idModulo || '');
    const secaoKey = String(idSecao || '');
    const setIds = new Set(safeArray(secoesConcluidasByModulo?.[moduloKey]).map((s) => String(s)));
    return Boolean(secaoKey && setIds.has(secaoKey));
  };

  const handleToggleSecaoConclusao = async (idModulo, idSecao, concluir) => {
    const moduloKey = String(idModulo || '');
    const secaoKey = String(idSecao || '');
    if (!moduloKey || !secaoKey) {
      showToast('Não foi possível identificar a seção para atualizar o progresso.', 3000, 'warning');
      return;
    }
    if (!idParticipanteTurma) {
      showToast('Aguarde alguns segundos: vínculo do participante com a turma ainda não foi identificado.', 3500, 'warning');
      debug.warn('handleToggleSecaoConclusao bloqueado: idParticipanteTurma ausente', {
        idModulo: moduloKey,
        idSecao: secaoKey,
      });
      return;
    }

    const actionKey = `${moduloKey}:${secaoKey}`;
    if (pendingSecaoActions[actionKey]) return;
    setPendingSecaoActions((prev) => ({ ...prev, [actionKey]: true }));

    const prevConcluidasByModulo = secoesConcluidasByModulo;
    const currentIds = new Set(safeArray(prevConcluidasByModulo?.[moduloKey]).map((s) => String(s)));
    if (concluir) currentIds.add(secaoKey);
    else currentIds.delete(secaoKey);

    const optimistic = {
      ...prevConcluidasByModulo,
      [moduloKey]: Array.from(currentIds),
    };

    await recalculateProgressAndPersist(optimistic);

    try {
      const secaoPayload = { idParticipanteTurma, idSecao: secaoKey };
      const resp = concluir
        ? await apiCall('marcarSecaoConcluida', secaoPayload, () => marcarSecaoConcluida(secaoPayload))
        : await apiCall('marcarSecaoNaoConcluida', secaoPayload, () => marcarSecaoNaoConcluida(secaoPayload));

      if (!resp?.success) {
        await recalculateProgressAndPersist(prevConcluidasByModulo);
        handleApiFailure(resp?.message, 'Não foi possível atualizar conclusão da seção.');
        return;
      }
    } catch (e) {
      await recalculateProgressAndPersist(prevConcluidasByModulo);
      debug.error('Erro ao atualizar conclusão da seção:', e);
      showToast('Erro de rede ao atualizar conclusão da seção.', 3500, 'error');
    } finally {
      setPendingSecaoActions((prev) => {
        const next = { ...prev };
        delete next[actionKey];
        return next;
      });
    }
  };

  const handleSubmitTeste = async (e) => {
    e?.preventDefault?.();

    const moduloId = String(selectedModulo?.id || '');
    const testeId = String(selectedModulo?.idTeste || '');
    const trilhaId = String(trilha?.id || '');
    if (!moduloId || !testeId || !trilhaId) {
      showToast('Modulo/teste externo invalido para envio.', 3500, 'error');
      return;
    }
    if (testeExternoSubmittingByModulo[moduloId]) return;

    if (!usuario?.id) {
      showToast('Faça login novamente para enviar suas respostas.', 3500, 'error');
      return redirectToLogin('handleSubmitTeste: usuario.id ausente');
    }

    if (!testeEstrutura?.questions || !selectedModulo?.idTeste) {
      showToast('Teste inválido. Recarregue a página.', 3500, 'error');
      return;
    }

    const respostasFormatadas = buildFormattedAnswers(testeEstrutura, respostasTeste);
    const estruturaPayload = {
      tipo: 'teste_externo_trilha',
      version: 1,
      idTrilha: String(trilha?.id || ''),
      idModulo: moduloId,
      idTeste: testeId,
      respostas: respostasTeste,
      respostasFormatadas,
      enviadoEm: new Date().toISOString(),
    };

    const payload = {
      idTrilha: trilhaId,
      idModulo: moduloId,
      idTeste: testeId,
      respostasJson: estruturaPayload,
    };

    setTesteExternoSubmittingByModulo((prev) => ({ ...prev, [moduloId]: true }));
    try {
      const resp = await apiCall(
        'upsertParticipanteModuloTesteResposta',
        payload,
        () => upsertParticipanteModuloTesteResposta(payload, token)
      );

      if (!resp?.success) {
        handleApiFailure(resp?.message, 'Nao foi possivel salvar as respostas do teste externo.');
        return;
      }

      const savedId =
        resp?.id ??
        resp?.data?.id ??
        resp?.data?.idParticipanteModuloTesteResposta ??
        testeExternoMetaAtual?.id ??
        `submitted:${moduloId}:${testeId}`;

      setTesteExternoRespostaMetaByModulo((prev) => ({
        ...prev,
        [moduloId]: {
          id: savedId,
          idTrilha: trilhaId,
          idModulo: moduloId,
          idTeste: testeId,
        },
      }));

      showToast(
        testeExternoHasRespostaSalva
          ? 'Resposta do teste externo atualizada com sucesso!'
          : 'Resposta do teste externo salva com sucesso!',
        3000,
        'success'
      );
    } catch (err) {
      debug.error('Erro ao salvar teste externo:', err);
      showToast(err?.message || 'Erro de rede ao salvar teste externo.', 3500, 'error');
    } finally {
      setTesteExternoSubmittingByModulo((prev) => ({ ...prev, [moduloId]: false }));
    }
  };

  const handleSubmitMiniTeste = async (e) => {
    e?.preventDefault?.();

    const moduloId = String(selectedModulo?.id || '');
    if (!moduloId) {
      showToast('Módulo inválido para envio do mini teste.', 3500, 'error');
      return;
    }
    if (miniTesteSubmittingByModulo[moduloId]) return;

    if (!usuario?.id) {
      showToast('Faça login novamente para enviar suas respostas.', 3500, 'error');
      return redirectToLogin('handleSubmitMiniTeste: usuario.id ausente');
    }

    if (!miniTesteEstrutura?.questions) {
      showToast('Mini teste inválido. Recarregue a página.', 3500, 'error');
      return;
    }

    const respostasFormatadas = buildFormattedAnswers(miniTesteEstrutura, respostasMiniTeste);
    const metaAtual = miniTesteRespostaMetaByModulo[moduloId] || null;
    const numeroTentativaAtual = toSafeNumber(metaAtual?.numeroTentativa, 0);

    if (metaAtual?.id) {
      showToast('Este mini teste já foi respondido.', 3000, 'info');
      return;
    }

    setMiniTesteSubmittingByModulo((prev) => ({ ...prev, [moduloId]: true }));
    try {
      const estruturaPayload = {
        respostas: respostasMiniTeste,
        respostasFormatadas,
      };

      const miniTestePayload = {
        idModulo: moduloId,
        estrutura: estruturaPayload,
        numeroTentativa: 1,
      };
      const resp = await apiCall(
        'criarModuloMiniTesteResposta',
        miniTestePayload,
        () => criarModuloMiniTesteResposta(miniTestePayload)
      );

      if (!resp?.success) {
        handleApiFailure(resp?.message, 'Não foi possível salvar as respostas do mini teste.');
        return;
      }

      const savedId =
        resp?.id ??
        resp?.data?.id ??
        resp?.data?.idModuloMiniTesteResposta ??
        metaAtual?.id ??
        `submitted:${moduloId}`;
      setMiniTesteRespostaMetaByModulo((prev) => ({
        ...prev,
        [moduloId]: {
          id: savedId,
          numeroTentativa:
            toSafeNumber(resp?.data?.numeroTentativa ?? resp?.data?.numero_tentativa, 0) ||
            numeroTentativaAtual + 1 ||
            1,
        },
      }));
      showToast('Respostas do mini teste salvas com sucesso!', 3000, 'success');
    } catch (err) {
      debug.error('Erro ao salvar mini teste:', err);
      showToast('Erro de rede ao salvar mini teste.', 3500, 'error');
    } finally {
      setMiniTesteSubmittingByModulo((prev) => ({ ...prev, [moduloId]: false }));
    }
  };

  // ===== LOGS DE MOUNT / PARAMS =====
  useEffect(() => {
    debug.groupCollapsed('mount');
    debug.log('params.idTurma:', idTurma);
    debug.log('usuario:', usuario);
    debug.log('URLS.IMG_PATH:', URLS?.IMG_PATH);
    debug.log('location:', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
    try {
      debug.log('idTurma decodeURIComponent:', decodeURIComponent(idTurma || ''));
    } catch (e) {
      debug.warn('decodeURIComponent falhou:', e);
    }
    debug.groupEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    debug.log('STATE idParticipanteTurma mudou ->', idParticipanteTurma ?? null);
  }, [idParticipanteTurma]);

  // ===== 1) Fluxo principal =====
  useEffect(() => {
    let cancelled = false;

    const carregar = async () => {
      debug.groupCollapsed('carregar (fluxo principal)');
      debug.log('start -> idTurma:', idTurma, 'usuario.id:', usuario?.id);

      if (!idTurma) {
        debug.warn('early return: idTurma vazio');
        debug.groupEnd();
        return;
      }

      if (!usuario?.id) {
        debug.warn('early return: usuário não logado');
        showToast('Faça login para acessar a trilha.', 3500, 'error');
        redirectToLogin('fluxo principal: usuario.id ausente');
        debug.groupEnd();
        return;
      }

      setIdParticipanteTurma(null);

      showL('Buscando dados da turma...');
      debug.time('fluxo principal total');

      try {
        // 1) Turma
        debug.time('obterTurmaPorId');
        debug.log('REQ obterTurmaPorId ->', { idTurma });
        const turmaRes = await apiCall('obterTurmaPorId', { idTurma }, () => obterTurmaPorId(idTurma));
        debug.timeEnd('obterTurmaPorId');
        debug.log('RES obterTurmaPorId ->', turmaRes);

        if (!turmaRes?.success) {
          showToast(turmaRes?.message || 'Erro ao carregar dados da turma.', 3000, 'error');

          if (turmaRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token inválido/expirado -> logout + redirect');
            logout();
            redirectToLogin('obterTurmaPorId: token');
            return;
          }

          debug.warn('falha obterTurmaPorId -> navigate /participante/page');
          navigate('/participante/page');
          return;
        }

        const turmaData = turmaRes.data;
        debug.table({
          turma_id: turmaData?.id,
          turma_nome: turmaData?.nome,
          status: turmaData?.status,
          data_limite: turmaData?.data_limite ?? turmaData?.dataLimite,
          idEmpresa: turmaData?.idEmpresa,
          idTrilha: turmaData?.idTrilha ?? turmaData?.id_trilha,
        });

        if (cancelled) return;

        setTurma(turmaData);

        // 2) Empresa (opcional)
        try {
          debug.time('obterEmpresaPorId');
          debug.log('REQ obterEmpresaPorId ->', { idEmpresa: turmaData?.idEmpresa });
          const empRes = await apiCall(
            'obterEmpresaPorId',
            { idEmpresa: turmaData.idEmpresa },
            () => obterEmpresaPorId(turmaData.idEmpresa)
          );
          debug.timeEnd('obterEmpresaPorId');
          debug.log('RES obterEmpresaPorId ->', empRes);

          if (empRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token na empresa -> logout + redirect');
            logout();
            redirectToLogin('obterEmpresaPorId: token');
            return;
          }

          if (empRes?.success && !cancelled) setEmpresa(empRes.data);
        } catch (e) {
          debug.warn('Erro ao buscar empresa (não bloqueia):', e);
        }

        // 3) Regras turma
        if (Number(turmaData?.status ?? 0) === 0) {
          debug.warn('turma inativa -> /participante/page');
          showToast('Essa turma ainda está inativa!', 4500, 'error');
          navigate('/participante/page');
          return;
        }

        const dataLimiteStr = turmaData?.data_limite || turmaData?.dataLimite;
        if (dataLimiteStr) {
          const hoje = new Date();
          const dataLimite = new Date(`${dataLimiteStr}T23:59:59`);
          debug.log('dataLimite check:', { hoje: hoje.toISOString(), dataLimite: dataLimite.toISOString() });

          if (hoje > dataLimite) {
            debug.warn('turma expirada -> /participante/page');
            showToast('O prazo para acessar essa turma expirou.', 4500, 'error');
            navigate('/participante/page');
            return;
          }
        }

        // 4) Vínculo participante x turma
        debug.time('obterParticipanteTurmaPorParticipanteETurma');
        debug.log('REQ obterParticipanteTurmaPorParticipanteETurma ->', { idTurma });
        const vinculoRes = await apiCall(
          'obterParticipanteTurmaPorParticipanteETurma (inicial)',
          { idTurma },
          () => obterParticipanteTurmaPorParticipanteETurma(idTurma)
        );
        debug.timeEnd('obterParticipanteTurmaPorParticipanteETurma');
        debug.log('RES obterParticipanteTurmaPorParticipanteETurma ->', vinculoRes);

        if (!vinculoRes?.success && String(vinculoRes?.message || '').toLowerCase().includes('token')) {
          debug.warn('token no vínculo participante/turma -> logout + redirect');
          logout();
          redirectToLogin('obterParticipanteTurmaPorParticipanteETurma: token');
          return;
        }

        const idPtFromVinculo =
          vinculoRes?.id ??
          vinculoRes?.data?.id ??
          vinculoRes?.data?.idParticipanteTurma ??
          vinculoRes?.data?.id_participante_turma ??
          null;
        const vinculado = Boolean(vinculoRes?.success && idPtFromVinculo);
        debug.table({
          vinculo_success: Boolean(vinculoRes?.success),
          vinculo_message: vinculoRes?.message ?? null,
          idPtFromVinculo: idPtFromVinculo ?? null,
          vinculado,
        });

        if (idPtFromVinculo) {
          debug.log('setIdParticipanteTurma (origem: readByParticipanteAndTurma inicial) ->', idPtFromVinculo);
          setIdParticipanteTurma(idPtFromVinculo);
        }

        if (!vinculado) {
          debug.time('associarParticipanteATurma');
          debug.log('REQ associarParticipanteATurma ->', { idParticipante: usuario.id, idTurma });
          const assocRes = await apiCall(
            'associarParticipanteATurma',
            { idParticipante: usuario.id, idTurma },
            () => associarParticipanteATurma(usuario.id, idTurma)
          );
          debug.timeEnd('associarParticipanteATurma');
          debug.log('RES associarParticipanteATurma ->', assocRes);

          if (!assocRes?.success) {
            showToast(assocRes?.message || 'Erro ao associar você à turma.', 3000, 'error');

            if (assocRes?.message?.toLowerCase?.().includes('token')) {
              debug.warn('token no associar -> logout + redirect');
              logout();
              redirectToLogin('associarParticipanteATurma: token');
              return;
            }

            debug.warn('falha associar -> /participante/page');
            navigate('/participante/page');
            return;
          }

          showToast('Vínculo com a turma realizado com sucesso!', 2500, 'success');

          // Busca vínculo novamente para obter idParticipanteTurma
          const vinculoPosAssocRes = await apiCall(
            'obterParticipanteTurmaPorParticipanteETurma (pos-associacao)',
            { idTurma },
            () => obterParticipanteTurmaPorParticipanteETurma(idTurma)
          );
          debug.log('RES obterParticipanteTurmaPorParticipanteETurma (pós-associação) ->', vinculoPosAssocRes);
          if (vinculoPosAssocRes?.success) {
            const idPtFromReadByParticipanteAndTurma =
              vinculoPosAssocRes?.id ??
              vinculoPosAssocRes?.data?.id ??
              vinculoPosAssocRes?.data?.idParticipanteTurma ??
              vinculoPosAssocRes?.data?.id_participante_turma ??
              null;
            const idPtFromAssocTop = assocRes?.idParticipanteTurma ?? assocRes?.id ?? null;
            const idPtFromAssocData = assocRes?.data?.idParticipanteTurma ?? assocRes?.data?.id ?? null;
            const idPt =
              idPtFromReadByParticipanteAndTurma ??
              idPtFromAssocTop ??
              idPtFromAssocData ??
              null;
            debug.table({
              idPtFromReadByParticipanteAndTurma: idPtFromReadByParticipanteAndTurma ?? null,
              idPtFromAssocTop: idPtFromAssocTop ?? null,
              idPtFromAssocData: idPtFromAssocData ?? null,
              idPtEscolhido: idPt ?? null,
            });
            if (idPt) {
              debug.log('setIdParticipanteTurma (origem: pós-associação) ->', idPt);
              setIdParticipanteTurma(idPt);
            } else {
              debug.error('Não foi possível resolver idParticipanteTurma nem pelo readByParticipanteAndTurma pós-associação nem pelo payload do create.', {
                assocRes,
                vinculoPosAssocRes,
              });
            }
          } else {
            debug.warn('Falha ao obter vínculo pós-associação (readByParticipanteAndTurma).', vinculoPosAssocRes);
          }
        }

        // 5) Trilha
        const idTrilha = turmaData?.idTrilha ?? turmaData?.id_trilha;
        if (!idTrilha) {
          debug.warn('turma sem idTrilha -> /participante/page');
          showToast('Esta turma não possui uma trilha vinculada.', 3500, 'warning');
          navigate('/participante/page');
          return;
        }

        debug.time('obterTrilhaPorId');
        debug.log('REQ obterTrilhaPorId ->', { idTrilha });
        const trilhaRes = await apiCall('obterTrilhaPorId', { idTrilha }, () => obterTrilhaPorId(idTrilha));
        debug.timeEnd('obterTrilhaPorId');
        debug.log('RES obterTrilhaPorId ->', trilhaRes);

        if (!trilhaRes?.success) {
          showToast(trilhaRes?.message || 'Erro ao carregar trilha.', 3000, 'error');

          if (trilhaRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token na trilha -> logout + redirect');
            logout();
            redirectToLogin('obterTrilhaPorId: token');
            return;
          }

          debug.warn('falha obterTrilhaPorId -> /participante/page');
          navigate('/participante/page');
          return;
        }

        if (cancelled) return;
        setTrilha(trilhaRes.data);

        // 6) Módulos
        debug.time('listarModulosPorTrilha');
        debug.log('REQ listarModulosPorTrilha ->', { idTrilha });
        const modRes = await apiCall(
          'listarModulosPorTrilha',
          { idTrilha },
          () => listarModulosPorTrilha(idTrilha)
        );
        debug.timeEnd('listarModulosPorTrilha');
        debug.log('RES listarModulosPorTrilha ->', modRes);

        if (!modRes?.success) {
          showToast(modRes?.message || 'Erro ao carregar módulos da trilha.', 3000, 'error');

          if (modRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token nos módulos -> logout + redirect');
            logout();
            redirectToLogin('listarModulosPorTrilha: token');
            return;
          }

          debug.warn('falha listarModulosPorTrilha -> /participante/page');
          navigate('/participante/page');
          return;
        }

        const lista = safeArray(modRes.data).slice().sort(sortByOrdem);
        debug.log('modulos (sorted) length:', lista.length);
        debug.table(lista.map(m => ({
          id: m?.id,
          titulo: m?.titulo,
          ordem: m?.ordem,
          idTeste: m?.idTeste,
          fechado: m?.fechado,
          data_abertura: m?.data_abertura
        })));

        if (cancelled) return;

        setModulos(lista);
        setModuloProgressMap({});
        setSecoesByModulo({});
        setSecoesConcluidasByModulo({});
        setTrilhaProgressPercent(0);
        setTrilhaProgressStatus('nao_iniciada');
        setMiniTesteRespostaMetaByModulo({});
        setTesteExternoRespostaMetaByModulo({});
        setTesteExternoSubmittingByModulo({});
        lastPersistedProgressRef.current = { percentual: null, status: null };

        // Reseta seleção/cache ao entrar na trilha
        setSelectedModuloId(null);
        setDetalhesCache({});
      } catch (e) {
        debug.error('catch fluxo principal:', e);
        showToast('Erro inesperado ao carregar a trilha.', 3000, 'error');
        navigate('/participante/page');
      } finally {
        debug.timeEnd('fluxo principal total');
        hideL();
        debug.groupEnd();
      }
    };

    carregar();

    return () => {
      cancelled = true;
      debug.log('cleanup efeito principal (cancelled=true)');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTurma, usuario?.id]);

  // ===== 2) Progresso (seções -> módulos -> trilha) =====
  useEffect(() => {
    let cancelled = false;

    const carregarProgresso = async () => {
      if (!trilha?.id || !modulos.length) return;
      if (!idParticipanteTurma) {
        if (!missingIdParticipanteTurmaWarnedRef.current) {
          missingIdParticipanteTurmaWarnedRef.current = true;
          debug.warn('carregarProgresso adiado: idParticipanteTurma ausente', {
            idTrilha: trilha?.id,
            modulos: modulos.length,
          });
        }
        return;
      }
      missingIdParticipanteTurmaWarnedRef.current = false;

      setLoadingProgresso(true);
      try {
        // Recupera status salvo anteriormente (não bloqueante)
        try {
          const saved = await apiCall(
            'obterTrilhaStatusPorParticipanteTurmaETrilha',
            { idParticipanteTurma, idTrilha: trilha.id },
            () => obterTrilhaStatusPorParticipanteTurmaETrilha(idParticipanteTurma, trilha.id)
          );
          if (saved?.success && !cancelled) {
            const data = saved?.data || {};
            const pct = toSafeNumber(data?.percentual ?? data?.progress ?? data?.percent, 0);
            const st = data?.status || resolveParticipanteTrilhaStatus(pct);
            setTrilhaProgressPercent(pct);
            setTrilhaProgressStatus(st);
          }
        } catch (e) {
          debug.warn('Falha ao ler status salvo da trilha (não bloqueante):', e);
        }

        const secoesEntries = await Promise.all(
          modulos.map(async (m) => {
            const idModulo = m?.id;
            if (!idModulo) return [String(idModulo || ''), []];
            try {
              const res = await apiCall(
                'listarSecoesPorModulo (progresso)',
                { idModulo },
                () => listarSecoesPorModulo(idModulo)
              );
              if (!res?.success) return [String(idModulo), []];
              return [String(idModulo), safeArray(res.data)];
            } catch {
              return [String(idModulo), []];
            }
          })
        );

        const concluidasEntries = await Promise.all(
          modulos.map(async (m) => {
            const idModulo = m?.id;
            if (!idModulo) return [String(idModulo || ''), []];
            try {
              const res = await apiCall(
                'listarSecoesConcluidasPorParticipanteTurmaEModulo',
                { idParticipanteTurma, idModulo },
                () => listarSecoesConcluidasPorParticipanteTurmaEModulo(idParticipanteTurma, idModulo)
              );
              if (!res?.success) return [String(idModulo), []];
              const rows = safeArray(res?.data);
              const ids = rows
                .map((r) => String(r?.idSecao ?? r?.id_secao ?? r?.id ?? ''))
                .filter(Boolean);
              return [String(idModulo), ids];
            } catch {
              return [String(idModulo), []];
            }
          })
        );

        if (cancelled) return;

        const nextSecoesByModulo = Object.fromEntries(secoesEntries);
        const nextConcluidasByModulo = Object.fromEntries(concluidasEntries);
        const nextModuloProgress = computeAllModuloProgress(modulos, nextSecoesByModulo, nextConcluidasByModulo);
        const nextTrilhaPercent = computeTrilhaPercentual(nextModuloProgress, modulos);
        const nextTrilhaStatus = resolveParticipanteTrilhaStatus(nextTrilhaPercent);

        setSecoesByModulo(nextSecoesByModulo);
        setSecoesConcluidasByModulo(nextConcluidasByModulo);
        setModuloProgressMap(nextModuloProgress);
        setTrilhaProgressPercent(nextTrilhaPercent);
        setTrilhaProgressStatus(nextTrilhaStatus);

        await persistTrilhaProgress(nextTrilhaPercent);
      } finally {
        if (!cancelled) setLoadingProgresso(false);
      }
    };

    carregarProgresso();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParticipanteTurma, trilha?.id, modulos]);

  // ===== 3) Carrega detalhes do módulo selecionado =====
  useEffect(() => {
    let cancelled = false;

    const carregarDetalhesModulo = async () => {
      debug.groupCollapsed('carregarDetalhesModulo');
      debug.log('selectedModuloId:', selectedModuloId);

      if (!selectedModuloId) {
        debug.log('early return: selectedModuloId vazio');
        debug.groupEnd();
        return;
      }

      if (detalhesCache[selectedModuloId]) {
        debug.log('cache hit -> detalhes já carregados');
        const cached = detalhesCache[selectedModuloId];
        if (cached?.respostasTestePersistidas && Object.keys(cached.respostasTestePersistidas).length) {
          setRespostasTeste(cached.respostasTestePersistidas);
        }
        if (cached?.testeExternoMeta?.id) {
          setTesteExternoRespostaMetaByModulo((prev) => ({
            ...prev,
            [String(selectedModuloId)]: cached.testeExternoMeta,
          }));
        }
        debug.groupEnd();
        return;
      }

      const modulo = modulos.find((m) => String(m.id) === String(selectedModuloId));
      if (!modulo) {
        debug.warn('modulo não encontrado no state modulos');
        debug.groupEnd();
        return;
      }

      showL('Carregando módulo...');
      debug.time(`detalhes modulo ${selectedModuloId}`);

      try {
        // seções
        debug.time('listarSecoesPorModulo');
        debug.log('REQ listarSecoesPorModulo ->', { idModulo: modulo.id });
        const secoesRes = await apiCall(
          'listarSecoesPorModulo (detalhes)',
          { idModulo: modulo.id },
          () => listarSecoesPorModulo(modulo.id)
        );
        debug.timeEnd('listarSecoesPorModulo');
        debug.log('RES listarSecoesPorModulo ->', secoesRes);

        if (!secoesRes?.success) {
          showToast(secoesRes?.message || 'Erro ao carregar seções do módulo.', 3000, 'error');

          if (secoesRes?.message?.toLowerCase?.().includes('token')) {
            debug.warn('token nas seções -> logout + redirect');
            logout();
            redirectToLogin('listarSecoesPorModulo: token');
            return;
          }

          return;
        }

        const secoes = safeArray(secoesRes.data).slice().sort(sortByOrdem);
        debug.log('secoes length:', secoes.length);
        debug.table(secoes.map(s => ({ id: s?.id, titulo: s?.titulo, ordem: s?.ordem })));

        // conteúdos (paralelo)
        debug.time('listarConteudosPorSecao (Promise.all)');
        const conteudosEntries = await Promise.all(
          secoes.map(async (secao) => {
            try {
              debug.log('REQ listarConteudosPorSecao ->', { idSecao: secao.id });
              const contRes = await apiCall(
                'listarConteudosPorSecao',
                { idSecao: secao.id },
                () => listarConteudosPorSecao(secao.id)
              );
              debug.log('RES listarConteudosPorSecao ->', { idSecao: secao.id, success: contRes?.success, message: contRes?.message });

              if (!contRes?.success) return [secao.id, []];

              const conteudos = safeArray(contRes.data).slice().sort(sortByOrdem);
              return [secao.id, conteudos];
            } catch (e) {
              debug.warn('Erro listarConteudosPorSecao:', { secaoId: secao?.id, e });
              return [secao.id, []];
            }
          })
        );
        debug.timeEnd('listarConteudosPorSecao (Promise.all)');

        const conteudosBySecao = Object.fromEntries(conteudosEntries);
        debug.log('conteudosBySecao keys:', Object.keys(conteudosBySecao));

        // Mini teste do módulo (opcional)
        const miniTesteEstrutura = parseQuestionsStructure(
          modulo?.mini_teste_estrutura ?? modulo?.miniTesteEstrutura ?? null
        );

        // Resposta do mini teste (se existir)
        if (miniTesteEstrutura?.questions?.length) {
          const moduloMiniTestePayload = { idModulo: modulo.id };
          let ultimaTentativa = null;
          let historicoTentativas = [];

          try {
            const ultimaResp = await apiCall(
              'readModuloMiniTesteRespostaUltimaTentativa',
              moduloMiniTestePayload,
              () => readModuloMiniTesteRespostaUltimaTentativa(moduloMiniTestePayload, token)
            );
            ultimaTentativa = ultimaResp?.data || null;
          } catch (e) {
            if (isRespostaNaoEncontrada(e?.message)) {
              debug.log('Mini teste sem última tentativa (estado inicial).', { idModulo: modulo.id });
            } else {
              debug.warn('Erro ao ler última tentativa do mini teste (não bloqueante):', e);
            }
          }

          try {
            const historicoResp = await apiCall(
              'readModuloMiniTesteRespostasByModuloUsuario',
              moduloMiniTestePayload,
              () => readModuloMiniTesteRespostasByModuloUsuario(moduloMiniTestePayload, token)
            );

            const rawHistorico = historicoResp?.data;
            historicoTentativas = Array.isArray(rawHistorico)
              ? rawHistorico
              : (rawHistorico && typeof rawHistorico === 'object' ? [rawHistorico] : []);
          } catch (e) {
            if (isRespostaNaoEncontrada(e?.message)) {
              debug.log('Mini teste sem histórico de tentativas (estado inicial).', { idModulo: modulo.id });
            } else {
              debug.warn('Erro ao ler histórico de tentativas do mini teste (não bloqueante):', e);
            }
          }

          const ultimaDoHistorico = historicoTentativas
            .slice()
            .sort((a, b) => {
              const na = toSafeNumber(a?.numeroTentativa ?? a?.numero_tentativa, 0);
              const nb = toSafeNumber(b?.numeroTentativa ?? b?.numero_tentativa, 0);
              return nb - na;
            })[0] || null;

          const tentativaAtual = ultimaTentativa || ultimaDoHistorico || null;

          if (tentativaAtual) {
            const respostaMeta = {
              id: tentativaAtual?.id ?? tentativaAtual?.idModuloMiniTesteResposta ?? null,
              numeroTentativa: toSafeNumber(tentativaAtual?.numeroTentativa ?? tentativaAtual?.numero_tentativa, 1),
            };

            if (respostaMeta.id) {
              setMiniTesteRespostaMetaByModulo((prev) => ({ ...prev, [String(modulo.id)]: respostaMeta }));
            }

            const estruturaResp = parseJsonLoose(tentativaAtual?.estrutura);
            const respostasNormalizadas = normalizeMiniTesteRespostasFromPersisted(
              miniTesteEstrutura?.questions || [],
              estruturaResp || {}
            );
            if (Object.keys(respostasNormalizadas).length) {
              setRespostasMiniTeste(respostasNormalizadas);
            }
          } else {
            setMiniTesteRespostaMetaByModulo((prev) => {
              const next = { ...prev };
              delete next[String(modulo.id)];
              return next;
            });
          }
        }

        // âœ… teste externo (opcional) -> agora carrega título E estrutura
        let testeTitulo = '';
        let testeEstrutura = null;
        let respostasTestePersistidas = {};
        let testeExternoMeta = null;

        if (modulo?.idTeste) {
          try {
            debug.time('obterTestePorId (modulo.idTeste)');
            debug.log('REQ obterTestePorId ->', { idTeste: modulo.idTeste });
            const testeRes = await apiCall(
              'obterTestePorId',
              { idTeste: modulo.idTeste },
              () => obterTestePorId(modulo.idTeste)
            );
            debug.timeEnd('obterTestePorId (modulo.idTeste)');
            debug.log('RES obterTestePorId ->', testeRes);

            if (testeRes?.success) {
              testeTitulo = testeRes?.data?.nome || '';

              const estruturaParse = parseQuestionsStructure(testeRes?.data?.estrutura);
              if (estruturaParse) {
                testeEstrutura = estruturaParse;
              } else {
                debug.warn('Estrutura do teste externo inválida/ausente (não bloqueia):', {
                  idTeste: modulo.idTeste,
                  hasEstrutura: Boolean(testeRes?.data?.estrutura),
                });
              }
            }
          } catch (e) {
            debug.warn('Erro ao obterTestePorId (não bloqueia):', e);
          }
        }

        // Resposta já existente do teste externo (se houver)
        if (modulo?.idTeste && testeEstrutura?.questions?.length) {
          const testeExternoPayload = { idModulo: modulo.id };
          try {
            let respostaTesteRes = await apiCall(
              'readParticipanteModuloTesteRespostaByParticipanteAndModulo',
              testeExternoPayload,
              () => readParticipanteModuloTesteRespostaByParticipanteAndModulo(testeExternoPayload, token)
            );

            let rawRows = respostaTesteRes?.data;
            let rows = Array.isArray(rawRows)
              ? rawRows
              : (rawRows && typeof rawRows === 'object' ? [rawRows] : []);

            if (!rows.length && trilha?.id) {
              const byTrilhaPayload = { idTrilha: trilha.id };
              respostaTesteRes = await apiCall(
                'readParticipanteModuloTesteRespostaByParticipanteAndTrilha',
                byTrilhaPayload,
                () => readParticipanteModuloTesteRespostaByParticipanteAndTrilha(byTrilhaPayload, token)
              );
              rawRows = respostaTesteRes?.data;
              rows = Array.isArray(rawRows)
                ? rawRows
                : (rawRows && typeof rawRows === 'object' ? [rawRows] : []);
            }

            const respostaAtual = rows.find(
              (row) =>
                String(row?.idModulo ?? row?.id_modulo ?? '') === String(modulo.id) &&
                String(row?.idTeste ?? row?.id_teste ?? '') === String(modulo.idTeste)
            ) || null;

            if (respostaAtual) {
              const meta = extractParticipanteModuloTesteRespostaMeta(respostaAtual);
              if (meta?.id) {
                testeExternoMeta = meta;
                setTesteExternoRespostaMetaByModulo((prev) => ({
                  ...prev,
                  [String(modulo.id)]: meta,
                }));
              }

              const estruturaResp = parseJsonLoose(
                respostaAtual?.respostas_json ?? respostaAtual?.respostasJson ?? respostaAtual?.respostas
              );
              const respostasNormalizadas = normalizeMiniTesteRespostasFromPersisted(
                testeEstrutura?.questions || [],
                estruturaResp || {}
              );
              if (Object.keys(respostasNormalizadas).length) {
                respostasTestePersistidas = respostasNormalizadas;
                setRespostasTeste(respostasNormalizadas);
              }
            } else {
              setTesteExternoRespostaMetaByModulo((prev) => {
                const next = { ...prev };
                delete next[String(modulo.id)];
                return next;
              });
            }
          } catch (e) {
            if (isRespostaNaoEncontrada(e?.message)) {
              debug.log('Teste externo sem resposta anterior (estado inicial).', { idModulo: modulo.id });
              setTesteExternoRespostaMetaByModulo((prev) => {
                const next = { ...prev };
                delete next[String(modulo.id)];
                return next;
              });
            } else {
              debug.warn('Erro ao ler resposta do teste externo (não bloqueante):', e);
            }
          }
        }

        if (cancelled) return;

        setDetalhesCache((prev) => ({
          ...prev,
          [selectedModuloId]: {
            secoes,
            conteudosBySecao,
            testeTitulo,
            testeEstrutura,
            miniTesteEstrutura,
            respostasTestePersistidas,
            testeExternoMeta,
          },
        }));
        setSecoesByModulo((prev) => ({
          ...prev,
          [String(selectedModuloId)]: secoes,
        }));
      } catch (e) {
        debug.error('catch detalhes modulo:', e);
        showToast('Erro inesperado ao carregar módulo.', 3000, 'error');
      } finally {
        debug.timeEnd(`detalhes modulo ${selectedModuloId}`);
        hideL();
        debug.groupEnd();
      }
    };

    carregarDetalhesModulo();

    return () => {
      cancelled = true;
      debug.log('cleanup efeito detalhes (cancelled=true)');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuloId, modulos, detalhesCache]);

  // ===== Render do conteúdo (com prefixo + PDF viewer) =====
  const renderConteudo = (c) => {
    const tipo = normalizeText(c?.tipo).toLowerCase();
    const titulo = normalizeText(c?.titulo);
    const texto = normalizeText(c?.texto);

    const urlImagemRaw = normalizeText(c?.url_imagem);
    const urlLinkRaw = normalizeText(c?.url_link);
    const urlVideo = normalizeText(c?.url_video);

    // Imagem / PDF (prefixo)
    const urlImagem = urlImagemRaw ? toAssetUrl(urlImagemRaw) : '';
    const urlLink = urlLinkRaw ? (isAbsoluteLike(urlLinkRaw) ? urlLinkRaw : toAssetUrl(urlLinkRaw)) : '';

    // Detecta PDF tanto no tipo quanto nas URLs
    const isPdf =
      tipo === 'pdf' ||
      looksLikePdf(urlLinkRaw) ||
      looksLikePdf(urlImagemRaw);

    // Lista
    let listaItens = c?.lista_itens;
    if (typeof listaItens === 'string') {
      try {
        const parsed = JSON.parse(listaItens);
        if (Array.isArray(parsed)) listaItens = parsed;
      } catch {
        // ignore
      }
    }
    listaItens = safeArray(listaItens);

    const embedUrl = urlVideo ? toYouTubeEmbedUrl(urlVideo) : null;

    // Se o PDF veio em url_imagem, prioriza isso
    const pdfUrlFinal = (() => {
      const a = urlLinkRaw || '';
      const b = urlImagemRaw || '';
      if (looksLikePdf(a)) return toAssetUrl(a);
      if (looksLikePdf(b)) return toAssetUrl(b);
      return urlLink || urlImagem || '';
    })();

    if (DEBUG) {
      debug.log('renderConteudo', {
        id: c?.id,
        tipo,
        titulo,
        urlImagemRaw,
        urlImagem,
        urlLinkRaw,
        urlLink,
        isPdf,
        pdfUrlFinal,
      });
    }

    return (
      <div key={c.id} className={styles.conteudoItem}>
        {titulo ? <div className={styles.conteudoTitulo}>{titulo}</div> : null}
        {texto ? <div className={styles.conteudoTexto}>{texto}</div> : null}

        {listaItens.length > 0 ? (
          <ul className={styles.conteudoLista}>
            {listaItens.map((it, idx) => (
              <li key={`${c.id}-li-${idx}`}>{String(it)}</li>
            ))}
          </ul>
        ) : null}

        {/* âœ… PDF: botão para abrir no modal */}
        {isPdf && pdfUrlFinal ? (
          <div className={styles.pdfBox}>
            <button
              type="button"
              className={styles.pdfOpenBtn}
              onClick={() => openPdf(pdfUrlFinal, titulo || 'Documento PDF')}
            >
              Visualizar PDF
            </button>
          </div>
        ) : null}

        {/* âœ… Imagem (prefixada) */}
        {!isPdf && urlImagem ? (
          <img
            src={urlImagem}
            alt={titulo || 'Imagem do conteúdo'}
            className={styles.conteudoImagem}
            onError={() => debug.warn('Falha ao carregar imagem:', urlImagem)}
          />
        ) : null}

        {/* âœ… Link (prefixado quando relativo) */}
        {!isPdf && urlLink ? (
          <a
            href={urlLink}
            target="_blank"
            rel="noreferrer"
            className={styles.conteudoLink}
          >
            Acessar link
          </a>
        ) : null}

        {/* âœ… Vídeo */}
        {urlVideo ? (
          embedUrl ? (
            <div className={styles.videoWrapper}>
              <iframe
                title={titulo || `video-${c.id}`}
                src={embedUrl}
                className={styles.videoFrame}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className={styles.mutedNote}>
              Link do vídeo inválido. Verifique a URL do YouTube.
            </div>
          )
        ) : null}

        {tipo && !texto && !listaItens.length && !urlImagemRaw && !urlLinkRaw && !urlVideo ? (
          <div className={styles.mutedNote}>Conteúdo do tipo “{tipo}”</div>
        ) : null}
      </div>
    );
  };

  // âœ… Loading fallback simples
  if (!turma || !trilha) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.containerTop}>
          <h1 className={styles.title}>Carregando trilha...</h1>
          {DEBUG && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#777' }}>
              <div><strong>idTurma:</strong> {String(idTurma || '(vazio)')}</div>
              <div><strong>usuario.id:</strong> {String(usuario?.id || '(vazio)')}</div>
              <div><strong>loaderCount:</strong> {String(loaderCountRef.current)}</div>
              <div><strong>IMG_PATH:</strong> {String(URLS?.IMG_PATH || '(vazio)')}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const scaleConfigTeste = buildScaleConfig(perguntaAtualTeste, respostasTeste[paginaAtualTeste]);
  const scaleConfigMiniTeste = buildScaleConfig(perguntaAtualMiniTeste, respostasMiniTeste[paginaAtualMiniTeste]);

  const renderQuestionario = ({
    keyPrefix,
    titulo,
    estrutura,
    question,
    respostaAtual,
    paginaAtual,
    respostas,
    setRespostas,
    embaralhamento,
    priorityOrder,
    dragState,
    scaleConfig,
    onChange,
    onPrev,
    onNext,
    onSubmit,
    onUp,
    onDown,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    answered,
    loadingText,
    helperText = null,
    submitDisabled = false,
    submitLabel = 'Enviar',
    showCorrectnessAfterSubmit = false,
    wasSubmitted = false,
    readOnly = false,
  }) => {
    const correctValue = getQuestionCorrectAnswer(question);
    const hasCorrectAnswer = correctValue !== null && typeof correctValue !== 'undefined';
    const currentValue = respostas?.[paginaAtual];
    const currentAnswered = isPerguntaRespondida(question, currentValue);
    const currentIsCorrect = showCorrectnessAfterSubmit && wasSubmitted && hasCorrectAnswer && currentAnswered
      ? evaluateIfAnswerIsCorrect(question, currentValue, correctValue)
      : null;

    return (
      <div className={styles.testeBox}>
      <div className={styles.testeTitle}>{titulo}</div>

      {estrutura?.questions?.length ? (
        <div className={styles.mutedNote} style={{ marginBottom: 10 }}>
          {helperText || <>Responda o teste abaixo e clique em <strong>Enviar</strong>.</>}
        </div>
      ) : (
        <div className={styles.mutedNote} style={{ marginBottom: 10 }}>
          {loadingText}
        </div>
      )}

      {estrutura?.questions?.length ? (
        <div style={{ marginTop: 16 }}>
          <div className={styles.perguntaWrapper}>
            <p className={styles.pergunta}>{question?.question}</p>
            {currentIsCorrect === true ? (
              <div className={styles.answerCorrectHint}>Sua resposta está correta.</div>
            ) : null}
            {currentIsCorrect === false ? (
              <div className={styles.answerIncorrectHint}>Sua resposta está incorreta.</div>
            ) : null}

            {question?.type === 'closed' && (
              <div className={styles.opcoesWrapper}>
                {Object.keys(question.options || {}).map((letra) => (
                  <div key={letra} className={styles.opcaoWrapper}>
                    <input
                      type="radio"
                      id={`${keyPrefix}_q${paginaAtual}_${letra}`}
                      name={`${keyPrefix}_resposta-${paginaAtual}`}
                      value={letra}
                      checked={respostas[paginaAtual] === letra}
                      onChange={() => onChange(paginaAtual, letra)}
                      className={styles.radioInput}
                      disabled={readOnly}
                      required
                    />
                    <label htmlFor={`${keyPrefix}_q${paginaAtual}_${letra}`} className={styles.opcaoLabel}>
                      {letra}) {embaralhamento[letra] ?? (question.options || {})[letra]}
                    </label>
                  </div>
                ))}
              </div>
            )}

            {question?.type === 'priority' && (
              <div className={styles.priorityWrapper}>
                <p className={styles.priorityHint}>
                  Arraste ou use as setas para ordenar (o topo é a <strong>maior prioridade</strong>).
                </p>

                <ul className={styles.priorityList}>
                  {priorityOrder.map((letra, idx) => (
                    <li
                      key={letra}
                      className={`${styles.priorityItem} ${dragState.overIdx === idx ? styles.dragOver : ''}`}
                      draggable={!readOnly}
                      onDragStart={onDragStart(idx)}
                      onDragOver={onDragOver(idx)}
                      onDrop={onDrop(idx)}
                      onDragEnd={onDragEnd}
                      title="Arraste para reordenar"
                    >
                      <span className={styles.dragHandle} aria-hidden="true">⋮⋮</span>
                      <span className={styles.priorityLetter}>{letra}</span>
                      <span className={styles.priorityText}>{(question.options || {})[letra]}</span>

                      <div className={styles.priorityActions}>
                        <button
                          type="button"
                          className={`${styles.priorityBtn} ${styles.priorityBtnUp}`}
                          aria-label="Mover para cima"
                          onClick={onUp(idx)}
                          disabled={readOnly || idx === 0}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <FaArrowUp />
                        </button>

                        <button
                          type="button"
                          className={`${styles.priorityBtn} ${styles.priorityBtnDown}`}
                          aria-label="Mover para baixo"
                          onClick={onDown(idx)}
                          disabled={readOnly || idx === priorityOrder.length - 1}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <FaArrowDown />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {question?.type === 'scale' && scaleConfig && (
              <div className={styles.scaleWrapper}>
                <div className={styles.scaleSliderHeader}>
                  <span className={styles.scaleMinLabel}>{scaleConfig.minLabel}</span>
                  <span className={styles.scaleCurrentValue}>
                    {scaleConfig.currentLabel}
                    {scaleConfig.currentLabel !== String(scaleConfig.valueNum) && <> ({scaleConfig.valueNum})</>}
                  </span>
                  <span className={styles.scaleMaxLabel}>{scaleConfig.maxLabel}</span>
                </div>

                <div className={styles.scaleSliderContainer}>
                  <input
                    type="range"
                    min={scaleConfig.min}
                    max={scaleConfig.max}
                    step={1}
                    value={scaleConfig.valueNum}
                    onChange={(e) => onChange(paginaAtual, Number(e.target.value))}
                    className={styles.scaleSlider}
                    disabled={readOnly}
                  />
                </div>

                <div className={styles.scaleTicks}>
                  {scaleConfig.ticks.map((tick) => (
                    <div key={tick.value} className={styles.scaleTick}>
                      <span className={styles.scaleTickLabel}>{tick.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {question?.type === 'open' && (
              question?.hasDate ? (
                <div className={styles.openWithDateWrapper}>
                  <textarea
                    placeholder="Digite sua resposta"
                    value={
                      respostaAtual && typeof respostaAtual === 'object'
                        ? (respostaAtual.text || '')
                        : (respostaAtual || '')
                    }
                    onChange={(e) => {
                      if (readOnly) return;
                      const newText = e.target.value;
                      setRespostas(prev => {
                        const prevVal = prev[paginaAtual];
                        const prevObj = (prevVal && typeof prevVal === 'object')
                          ? prevVal
                          : { text: '', date: '' };
                        return { ...prev, [paginaAtual]: { ...prevObj, text: newText } };
                      });
                    }}
                    className={styles.textInput}
                    rows={5}
                    disabled={readOnly}
                    required
                  />

                  <input
                    type="date"
                    value={respostaAtual && typeof respostaAtual === 'object' ? (respostaAtual.date || '') : ''}
                    onChange={(e) => {
                      if (readOnly) return;
                      const newDate = e.target.value;
                      setRespostas(prev => {
                        const prevVal = prev[paginaAtual];
                        const prevObj = (prevVal && typeof prevVal === 'object')
                          ? prevVal
                          : { text: '', date: '' };
                        return { ...prev, [paginaAtual]: { ...prevObj, date: newDate } };
                      });
                    }}
                    className={styles.dateInput}
                    disabled={readOnly}
                    required
                  />
                </div>
              ) : (
                <textarea
                  placeholder="Digite sua resposta"
                  value={typeof respostaAtual === 'string' ? respostaAtual : ''}
                  onChange={(e) => onChange(paginaAtual, e.target.value)}
                  className={styles.textInput}
                  rows={5}
                  disabled={readOnly}
                  required
                />
              )
            )}
          </div>

          <div className={styles.navigationArea} style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={onPrev}
              disabled={paginaAtual === 0}
              className={styles.navBackButton}
            >
              Anterior
            </button>

            {paginaAtual < (estrutura?.questions?.length || 0) - 1 ? (
              <button
                type="button"
                onClick={onNext}
                className={styles.navButton}
                disabled={!readOnly && !answered}
              >
                Próxima
              </button>
            ) : (
              <button
                type="button"
                onClick={onSubmit}
                className={styles.submitButton}
                disabled={!answered || submitDisabled}
              >
                {submitLabel}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      {/* âœ… Modal PDF */}
      <PdfViewerModal
        open={pdfViewer.open}
        title={pdfViewer.title}
        url={pdfViewer.url}
        onClose={closePdf}
      />

      {selectedModuloId ? (
        <button
          type="button"
          className={styles.floatingBackArrow}
          onClick={() => setSelectedModuloId(null)}
          title="Voltar para modulos"
          aria-label="Voltar para modulos"
        >
          <FaArrowLeft />
        </button>
      ) : null}

      {!selectedModuloId ? (
        <div className={styles.containerTop}>
          <div className={styles.heroHeader}>
            <div className={styles.heroImageWrap} aria-hidden="true">
              <div className={styles.heroImageMock} />
            </div>

            <div className={styles.heroContent}>
              <h1 className={styles.title}>{trilha?.titulo ? trilha.titulo : 'Trilha'}</h1>

              {trilha?.objetivos ? (
                <div className={styles.heroObjetivoBlock}>
                  <p className={styles.heroObjetivo}>
                    <span className={styles.heroMetaLabel}>Objetivo:</span>
                  </p>
                  <div className={`${styles.markdownContent} ${styles.heroObjetivoBody}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {trilha.objetivos}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : null}

              {(trilha?.cargaHoraria || trilha?.carga_horaria) ? (
                <p className={styles.heroCargaHoraria}>
                  <span className={styles.heroMetaLabel}>Carga horaria:</span>{' '}
                  {trilha?.cargaHoraria || trilha?.carga_horaria}
                </p>
              ) : null}

              <p className={styles.heroTurma}>
                <span className={styles.heroMetaLabel}>Turma:</span>{' '}
                {turma?.nome ? turma.nome : 'Nao informada'}
              </p>

              {empresa?.nome ? (
                <p className={styles.heroEmpresa}>
                  <span className={styles.heroMetaLabel}>Empresa:</span> {empresa.nome}
                </p>
              ) : null}

              <div className={styles.trilhaProgressBlock}>
                <div className={styles.moduloProgressHeader}>
                  <span className={styles.moduloProgressLabel}>Progresso da trilha</span>
                  <span className={styles.moduloProgressValue}>{Math.round(trilhaProgressPercent)}%</span>
                </div>
                <div className={styles.moduloProgressTrack} aria-hidden="true">
                  <div className={styles.moduloProgressFill} style={{ width: `${Math.round(trilhaProgressPercent)}%` }} />
                </div>
                <div className={styles.mutedNote}>
                  Status: {trilhaProgressStatus === 'concluida'
                    ? 'Concluída'
                    : trilhaProgressStatus === 'em_andamento'
                      ? 'Em andamento'
                      : 'Não iniciada'}
                  {loadingProgresso ? ' • Atualizando...' : ''}
                </div>
              </div>
            </div>
          </div>

          {trilha?.descricao ? (
            <div className={styles.heroDescriptionBlock}>
              <div className={`${styles.subTitle} ${styles.markdownContent}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {trilha.descricao}
                </ReactMarkdown>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {!selectedModuloId && (
        <div className={styles.formulario}>
          {modulos.length === 0 ? (
            <div className={styles.perguntaWrapper}>
              <div className={styles.pergunta}>Nenhum módulo disponível</div>
              <div className={styles.mutedNote}>
                Esta trilha ainda não possui módulos cadastrados.
              </div>
            </div>
          ) : (
            <div className={styles.modulosList}>
              {modulos.map((m, idx) => {
                const locked = isModuloFechado(m);
                const dataAbertura = getModuloDataAberturaLabel(m);
                const progress = getModuloCompletionPercent(m, idx);

                const title = locked
                  ? `Módulo ainda fechado${dataAbertura ? ` (abre em ${dataAbertura})` : ''}`
                  : (m?.descricao || '');

                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`${styles.moduloListItem} ${locked ? (styles.moduloListItemDisabled || '') : ''}`}
                    onClick={() => {
                      if (locked) return; // redundância, pois disabled já bloqueia
                      setSelectedModuloId(m.id);
                    }}
                    title={title}
                    disabled={locked}
                    aria-disabled={locked}
                    style={locked ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                  >
                    <div className={styles.moduloListTop}>
                      <div className={styles.moduloListTitleBlock}>
                        <div className={styles.moduloListTitle}>{m.titulo}</div>
                        {m?.descricao ? (
                          <div className={`${styles.moduloListDesc} ${styles.markdownContent} ${styles.moduloListDescMarkdown}`}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                              {m.descricao}
                            </ReactMarkdown>
                          </div>
                        ) : null}
                      </div>

                      <span
                        className={`${styles.moduloStatusPill} ${locked ? styles.moduloStatusLocked : styles.moduloStatusOpen}`}
                      >
                        {locked ? 'Fechado' : 'Disponivel'}
                      </span>
                    </div>

                    <div className={styles.moduloProgressHeader}>
                      <span className={styles.moduloProgressLabel}>Conclusao</span>
                      <span className={styles.moduloProgressValue}>{progress}%</span>
                    </div>

                    <div className={styles.moduloProgressTrack} aria-hidden="true">
                      <div className={styles.moduloProgressFill} style={{ width: `${progress}%` }} />
                    </div>

                    {locked ? (
                      <div className={styles.mutedNote} style={{ marginTop: 8 }}>
                        🔒 Módulo fechado{dataAbertura ? ` • Abre em ${dataAbertura}` : ''}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedModuloId && selectedModulo && (
        <div className={styles.formulario}>
          <div className={styles.perguntaWrapper}>
            {selectedModulo?.descricao ? (
              <div className={`${styles.mutedNote} ${styles.markdownContent}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {selectedModulo.descricao}
                </ReactMarkdown>
              </div>
            ) : null}

            {miniTesteEstrutura?.questions?.length ? (
              renderQuestionario({
                keyPrefix: 'mini',
                titulo: 'Mini teste do módulo',
                estrutura: miniTesteEstrutura,
                question: perguntaAtualMiniTeste,
                respostaAtual: respostaAtualMiniTeste,
                paginaAtual: paginaAtualMiniTeste,
                respostas: respostasMiniTeste,
                setRespostas: setRespostasMiniTeste,
                embaralhamento: embaralhamentoAtualMiniTeste,
                priorityOrder: priorityOrderMiniTeste,
                dragState: dragStateMiniTeste,
                scaleConfig: scaleConfigMiniTeste,
                onChange: handleChangeMiniTeste,
                onPrev: handleAnteriorMiniTeste,
                onNext: handleProximoMiniTeste,
                onSubmit: handleSubmitMiniTeste,
                onUp: handleClickUpMiniTeste,
                onDown: handleClickDownMiniTeste,
                onDragStart: onDragStartPriorityMiniTeste,
                onDragOver: onDragOverPriorityMiniTeste,
                onDrop: onDropPriorityMiniTeste,
                onDragEnd: onDragEndPriorityMiniTeste,
                answered: perguntaRespondidaMiniTeste,
                loadingText: 'Carregando estrutura do mini teste...',
                helperText: 'Responda o mini teste e clique em Enviar.',
                submitDisabled:
                  Boolean(miniTesteSubmittingByModulo[String(selectedModulo?.id || '')]) ||
                  Boolean(miniTesteRespostaMetaByModulo[String(selectedModulo?.id || '')]?.id),
                submitLabel: miniTesteSubmittingByModulo[String(selectedModulo?.id || '')]
                  ? 'Enviando...'
                  : 'Enviar',
                showCorrectnessAfterSubmit: true,
                wasSubmitted: Boolean(miniTesteRespostaMetaByModulo[String(selectedModulo?.id || '')]?.id),
                readOnly: miniTesteAlreadySubmitted,
              })
            ) : null}

            {selectedModulo?.idTeste ? (
              renderQuestionario({
                keyPrefix: 'ext',
                titulo: detalhesSelecionado?.testeTitulo ? detalhesSelecionado.testeTitulo : 'Teste externo',
                estrutura: testeEstrutura,
                question: perguntaAtualTeste,
                respostaAtual: respostaAtualTeste,
                paginaAtual: paginaAtualTeste,
                respostas: respostasTeste,
                setRespostas: setRespostasTeste,
                embaralhamento: embaralhamentoAtualTeste,
                priorityOrder: priorityOrderTeste,
                dragState: dragStateTeste,
                scaleConfig: scaleConfigTeste,
                onChange: handleChangeTeste,
                onPrev: handleAnteriorTeste,
                onNext: handleProximoTeste,
                onSubmit: handleSubmitTeste,
                onUp: handleClickUpTeste,
                onDown: handleClickDownTeste,
                onDragStart: onDragStartPriorityTeste,
                onDragOver: onDragOverPriorityTeste,
                onDrop: onDropPriorityTeste,
                onDragEnd: onDragEndPriorityTeste,
                answered: perguntaRespondidaTeste,
                loadingText: 'Carregando estrutura do teste...',
                helperText: testeExternoHasRespostaSalva
                  ? 'Resposta anterior carregada. Voce pode alterar e enviar novamente.'
                  : 'Responda o teste externo e clique em Enviar.',
                submitDisabled: Boolean(testeExternoSubmittingByModulo[String(selectedModulo?.id || '')]),
                submitLabel: testeExternoSubmittingByModulo[String(selectedModulo?.id || '')]
                  ? 'Enviando...'
                  : (testeExternoHasRespostaSalva ? 'Atualizar teste externo' : 'Enviar teste externo'),
                showCorrectnessAfterSubmit: false,
                wasSubmitted: false,
              })
            ) : null}
          </div>

          {(detalhesSelecionado?.secoes || []).map((secao) => {
            const conteudos = detalhesSelecionado?.conteudosBySecao?.[secao.id] || [];
            const secaoConcluida = isSecaoConcluida(selectedModulo?.id, secao?.id);
            const actionKey = `${String(selectedModulo?.id || '')}:${String(secao?.id || '')}`;
            const secaoPending = Boolean(pendingSecaoActions[actionKey]);

            return (
              <div key={secao.id} className={styles.perguntaWrapper}>
                <div className={styles.secaoHeader}>
                  <div className={styles.pergunta}>{secao.titulo}</div>
                  <button
                    type="button"
                    className={secaoConcluida ? styles.secaoDoneBtn : styles.secaoPendingBtn}
                    onClick={() => handleToggleSecaoConclusao(selectedModulo?.id, secao?.id, !secaoConcluida)}
                    disabled={secaoPending}
                  >
                    {secaoPending
                      ? 'Salvando...'
                      : secaoConcluida
                        ? 'Desfazer conclusão'
                        : 'Marcar como concluída'}
                  </button>
                </div>

                {secao?.descricao ? (
                  <div className={`${styles.mutedNote} ${styles.markdownContent}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {secao.descricao}
                    </ReactMarkdown>
                  </div>
                ) : null}

                <div className={styles.opcoesWrapper}>
                  {conteudos.length === 0 ? (
                    <div className={styles.mutedNote}>Nenhum conteúdo cadastrado nesta seção.</div>
                  ) : (
                    conteudos.map(renderConteudo)
                  )}
                </div>
              </div>
            );
          })}

          {detalhesSelecionado && detalhesSelecionado.secoes?.length === 0 ? (
            <div className={styles.perguntaWrapper}>
              <div className={styles.pergunta}>Sem seções</div>
              <div className={styles.mutedNote}>Este módulo ainda não possui seções cadastradas.</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default EadTrilhaPage;

